import React from 'react'
import { Text, FormLabel } from '@cinch-labs/design-system'
import { Secondary, TextField } from '@cinch-labs/design-system'
import cx from 'classnames'
import {
  splitVariantDescription,
  normalizeVrm,
  capitalize,
} from '../../find-your-car.utils'
import styles from './find-by-registration.module.css'
import { useConsumerValuations } from '@cinch-labs/data-consumer-valuations'
import { useMaintenanceContext } from '../../../../context/maintenance-context'
import axios from 'axios'

export interface FindByRegistrationProps {
  vrm: string
  setVrm: (v: string) => void
  error: string
  loading: boolean
  setError: React.Dispatch<React.SetStateAction<string>>
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  onSelectFoundVariant?: (variant: FoundVariant) => void
  onRegistrationSearch?: () => void
  vrmForFoundVariants?: string
}

export interface FoundVariant {
  id: string
  make: string
  description: string
  vin?: string
  fuelType?: string
}

export const FindByRegistration: React.FC<FindByRegistrationProps> = ({
  vrm,
  setVrm,
  error,
  loading,
  setError,
  setLoading,
  onSelectFoundVariant,
  onRegistrationSearch,
  vrmForFoundVariants,
}) => {
  const { dispatch, foundVariants = [] } = useMaintenanceContext()
  const { getVariants } = useConsumerValuations()

  const handleFindByRegistration = async () => {
    if (!vrm.trim()) {
      setError('Please enter a registration number')
      return
    }
    if (!/^[A-Z0-9 ]{1,10}$/i.test(vrm)) {
      setError('Please enter a valid car registration (e.g. A20 FGG)')
      return
    }

    setError('')
    setLoading(true)

    if (onRegistrationSearch) {
      onRegistrationSearch()
    }

    try {
      const cleanVrm = normalizeVrm(vrm)
      const variants = await getVariants(cleanVrm)

      const noVariants =
        !variants || !variants.variants || variants.variants.length === 0

      if (noVariants) {
        setError(
          "We couldn't find any cars with that registration. Please check you entered it correctly",
        )
        return
      }

      dispatch({ type: 'SET_SEARCHED_VRM', searchedVrm: vrm })
      dispatch({
        type: 'SET_FOUND_VARIANTS',
        foundVariants: variants.variants,
        mileage: variants.motOdometerValue,
      })

      if (variants.variants.length > 0) {
        handleVariantClick(variants.variants[0])
      }
    } catch (error_) {
      if (axios.isAxiosError(error_) && error_.response?.status === 404) {
        setError(
          "We couldn't find any cars with that registration. Please check you entered it correctly",
        )
      }
    } finally {
      setLoading(false)
    }
  }

  const handleVariantClick = (variant: FoundVariant) => {
    onSelectFoundVariant?.(variant)
  }

  return (
    <div className={styles.formContainer}>
      <div className={styles.formItem}>
        <FormLabel
          htmlFor="vehicle_registration_input"
          classNames={{ label: styles.formLabel }}
        >
          <Text element="span" fontSizeFixed="s" fontWeight="semibold">
            Car registration
          </Text>
        </FormLabel>

        <TextField
          fullWidth
          value={vrm}
          onChange={(e) => setVrm(e.target.value)}
          maxLength={10}
          aria-label="Registration number"
          error={{ show: !!error, message: error, collapse: true }}
          classNames={{
            label: { label: styles.formLabel },
            container: styles.formTextField,
            input: styles.formTextFieldInput,
          }}
        />
      </div>

      <Secondary
        onClick={handleFindByRegistration}
        aria-label="Find car"
        classNames={{ button: styles.formButton }}
        disabled={loading}
        size="large"
      >
        {loading ? 'Searching…' : 'Find car'}{' '}
      </Secondary>

      {foundVariants.length > 0 &&
        onSelectFoundVariant &&
        vrmForFoundVariants &&
        foundVariants[0] && (
          <div>
            <ul className={styles.vehicleOrdersList}>
              <li key={foundVariants[0].id}>
                <button
                  type="button"
                  aria-label={`Select ${foundVariants[0].make} ${
                    foundVariants[0].description
                  } (${normalizeVrm(vrmForFoundVariants)})`}
                  className={cx(styles.foundCarCard)}
                  onClick={() => {
                    handleVariantClick(foundVariants[0])
                  }}
                  tabIndex={0}
                >
                  <div className={styles.foundCarTextContent}>
                    <span className={styles.foundCarMake}>
                      {`${capitalize(foundVariants[0].make)} ${capitalize(
                        splitVariantDescription(foundVariants[0].description)
                          .model,
                      )}`}
                    </span>
                  </div>

                  <span className={styles.foundCarVrmBox}>
                    {normalizeVrm(vrmForFoundVariants)}
                  </span>
                </button>
              </li>
            </ul>
          </div>
        )}
    </div>
  )
}
